import React from 'react';
import { Plus, ChevronRight } from 'lucide-react';
import { getLogoBase64 } from '../data/toolData';

/**
 * RecommendationsPanel - Minimal sidebar showing AI tool recommendations
 */
export default function RecommendationsPanel({ 
  recommendations = [],
  toolData = [],
  onAddToStack,
  onSelectTool,
  onClose,
  isVisible = false,
}) {
  const tools = recommendations
    .map(name => toolData.find(t => t.name.toLowerCase() === name.toLowerCase()))
    .filter(Boolean);

  const toolCount = tools.length;

  if (!isVisible || toolCount === 0) return null;

  // Single tool - minimal inline badge
  if (toolCount === 1) {
    const tool = tools[0];
    const logoBase64 = getLogoBase64(tool.name);
    
    return (
      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 animate-slide-in-left">
        <button
          onClick={() => onSelectTool?.(tool)}
          className="group flex items-center gap-3 px-3 py-2.5 bg-zinc-900/80 backdrop-blur border border-zinc-800 hover:border-zinc-700 rounded-xl transition-all"
        >
          {logoBase64 ? (
            <img src={logoBase64} alt={tool.name} className="w-8 h-8 rounded-lg object-contain" style={{ backgroundColor: tool.bg }} />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: tool.bg || '#3f3f46' }}>
              {tool.name.charAt(0)}
            </div>
          )}
          <div className="text-left">
            <div className="text-sm text-white">{tool.name}</div>
            <div className="text-[10px] text-zinc-500">Best match</div>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
        </button>
      </div>
    );
  }

  // Multiple tools - subtle vertical list
  return (
    <div className="fixed left-4 top-28 z-40 w-48 animate-slide-in-left">
      <div className="bg-zinc-900/80 backdrop-blur border border-zinc-800 rounded-xl overflow-hidden">
        {/* Minimal header */}
        <div className="px-3 py-2 border-b border-zinc-800/50">
          <span className="text-xs text-zinc-500">{toolCount} matches</span>
        </div>
        
        {/* Tool list */}
        <div className="max-h-[300px] overflow-y-auto">
          {tools.slice(0, 5).map((tool) => {
            const logoBase64 = getLogoBase64(tool.name);
            return (
              <button
                key={tool.id}
                onClick={() => onSelectTool?.(tool)}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-zinc-800/50 transition-colors text-left"
              >
                {logoBase64 ? (
                  <img src={logoBase64} alt={tool.name} className="w-6 h-6 rounded object-contain" style={{ backgroundColor: tool.bg }} />
                ) : (
                  <div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: tool.bg || '#3f3f46' }}>
                    {tool.name.charAt(0)}
                  </div>
                )}
                <span className="text-sm text-zinc-300 truncate flex-1">{tool.name}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onAddToStack?.(tool); }}
                  className="p-1 hover:bg-zinc-700 rounded text-zinc-600 hover:text-teal-400 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
